// Homepage hero. Text, the phone number and the background (photo or video) are
// editable in the admin (Website → Homepage); empty fields fall back to
// src/data/homeDefaults/hero.js and to the built-in clip below.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./Hero.css";
import Button from "../../components/button/Button";
import { useHomeContent } from "../../components/HomeContent";
import { E, useEd } from "../../components/Editable";
import { framed } from "../../utils/framed";

// Mobile videos (900p, CRF 28)
import clip1WebmMobile from "../../assets/home/hero/security-guard-montreal-hero-mobile.webm";
import clip1Mp4Mobile from "../../assets/home/hero/security-guard-montreal-hero-mobile.mp4";

// Desktop videos (1080p, CRF 26)
import clip1WebmDesktop from "../../assets/home/hero/security-guard-montreal-hero-desktop.webm";
import clip1Mp4Desktop from "../../assets/home/hero/security-guard-montreal-hero-desktop.mp4";

// Poster images (shown instantly while video buffers)
import posterMobileWebp from "../../assets/home/hero/hero-poster-mobile.webp";
import posterMobileJpg from "../../assets/home/hero/hero-poster-mobile.jpg";
import posterDesktopWebp from "../../assets/home/hero/hero-poster-desktop.webp";
import posterDesktopJpg from "../../assets/home/hero/hero-poster-desktop.jpg";

const CLIP_DURATION_MS = 27000;

const telHref = (phone) => `tel:+1${String(phone).replace(/\D/g, "").replace(/^1(?=\d{10}$)/, "")}`;
// Videos uploaded in the admin are MP4 or WebM
const videoType = (src) => (/\.webm(?:[?#]|$)/i.test(src) ? "video/webm" : "video/mp4");

// Text that shares its element with other content: the bare text on the public
// site (markup unchanged), an editable <span> in the admin's on-page editor.
function Txt({ children, ...props }) {
  const ed = useEd();
  return ed.editing ? <E {...props}>{children}</E> : children ?? null;
}

const usePrefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* ---- tiny reveal-on-scroll hook ---- */
function useReveal(options = { threshold: 0.18, rootMargin: "0px 0px -10% 0px" }) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!ref.current || inView) return;
    if (!("IntersectionObserver" in window)) {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true);
        io.disconnect();
      }
    }, options);
    io.observe(ref.current);
    return () => io.disconnect();
  }, [inView, options]);

  return [ref, inView];
}

const Hero = () => {
  const { c, en, isFR } = useHomeContent();
  const h = c.hero;
  const ed = useEd();
  const navigate = useNavigate();
  const reduceMotion = usePrefersReducedMotion();

  // The phone number is entered once (English side) and used for both languages
  const phone = en.hero.phone || "";

  // Background from the admin: a photo replaces the built-in video, a video replaces
  // the clip (its cover image, if any, shows while it loads).
  const media = h.media?.url ? h.media : null;
  const cmsVideo = media && media.type === "video" ? media.url : null;
  const cmsPhoto = media && !cmsVideo ? media.url : null;
  const bgAlt = (cmsPhoto && media.alt) || "";
  // Focus point / zoom from the admin â€” also used on the built-in clip and poster
  const framing = h.media?.framing;

  // Detect if viewport is mobile (< 768px) — SSR/prerender-safe default.
  const [isMobile, setIsMobile] = useState(false);
  const [videoReady, setVideoReady] = useState(false);

  useEffect(() => {
    // Skip during prerender so prerendered HTML matches client first render.
    if (typeof navigator !== 'undefined' && navigator.userAgent.includes('HeadlessChrome')) return;
    setIsMobile(window.innerWidth < 768);
    let timer;
    const handleResize = () => {
      clearTimeout(timer);
      timer = setTimeout(() => setIsMobile(window.innerWidth < 768), 150);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Poster image (shown instantly, hidden once video plays)
  const posterSrc = isMobile ? posterMobileWebp : posterDesktopWebp;
  const posterFallback = media ? cmsPhoto || media.poster : isMobile ? posterMobileJpg : posterDesktopJpg;

  // Select video sources based on viewport
  const sources = useMemo(
    () => {
      if (cmsPhoto) return [];
      if (cmsVideo) {
        const clip = [{ src: cmsVideo, type: videoType(cmsVideo) }];
        return [clip, clip];
      }
      const webmSrc = isMobile ? clip1WebmMobile : clip1WebmDesktop;
      const mp4Src = isMobile ? clip1Mp4Mobile : clip1Mp4Desktop;
      return [
        [
          { src: webmSrc, type: "video/webm" },
          { src: mp4Src, type: "video/mp4" }
        ],
        [
          { src: webmSrc, type: "video/webm" },
          { src: mp4Src, type: "video/mp4" }
        ],
      ];
    },
    [isMobile, cmsVideo, cmsPhoto]
  );

  // double-video crossfade
  const [active, setActive] = useState(0);
  const refs = [useRef(null), useRef(null)];

  const loadClip = (videoEl, i, isFirst = false) => {
    if (!videoEl) return;
    // React never writes `muted` into the HTML and doesn't set it when hydrating
    // prerendered markup, so set it here: browsers only autoplay muted video.
    videoEl.muted = true;
    videoEl.defaultMuted = true;
    videoEl.setAttribute("muted", "");
    videoEl.innerHTML = "";
    sources[i % sources.length].forEach(({ src, type }) => {
      const s = document.createElement("source");
      s.src = src;
      s.type = type;
      videoEl.appendChild(s);
    });
    videoEl.load();
    videoEl.currentTime = 0;
    if (isFirst) {
      const onPlay = () => {
        setVideoReady(true);
        videoEl.removeEventListener("playing", onPlay);
      };
      videoEl.addEventListener("playing", onPlay);
    }
    videoEl.play().catch(() => {});
  };

  useEffect(() => {
    if (reduceMotion || sources.length === 0) return;
    // Don't inject <source> tags during prerender: the snapshot would then contain
    // children React never renders, and hydration fails on the <video>.
    if (typeof navigator !== 'undefined' && navigator.userAgent.includes('HeadlessChrome')) return;

    loadClip(refs[0].current, 0, true);
    // Delay loading the second video — no need to fetch it immediately
    const delaySecond = setTimeout(() => loadClip(refs[1].current, 1), 3000);

    let current = 1;
    let nextIndex = 0;

    const interval = setInterval(() => {
      setActive((a) => 1 - a);
      const hiddenVideo = refs[current].current;
      nextIndex = (nextIndex + 1) % sources.length;
      loadClip(hiddenVideo, nextIndex);
      current = 1 - current;
    }, CLIP_DURATION_MS);

    return () => { clearInterval(interval); clearTimeout(delaySecond); };
  }, [reduceMotion, sources, sources.length]);

  // reveal hooks for content & CTA
  const [contentRef, contentIn] = useReveal();
  const [ctaRef, ctaIn] = useReveal();

  return (
    <section id="hero" className="hero">
      {!reduceMotion && (
        <div className="hero__bg" {...ed.img("hero.media", { media: true })}>
          {/* Poster shown instantly while video buffers */}
          {posterFallback && (
            <picture
              className={`hero__poster-img ${videoReady && !cmsPhoto ? "hero__poster-img--hidden" : ""}`}
              aria-hidden={bgAlt ? undefined : "true"}
            >
              {!media && <source srcSet={posterSrc} type="image/webp" />}
              <img
                src={posterFallback}
                alt={bgAlt}
                {...framed(framing, "hero__poster-img-el")}
                fetchPriority="high"
                width={1280}
                height={720}
              />
            </picture>
          )}
          {!cmsPhoto && (
            <>
              <video
                ref={refs[0]}
                {...framed(framing, `hero__video ${active === 0 ? "is-active" : ""}`)}
                playsInline
                muted
                autoPlay
                loop
                preload="metadata"
                poster={posterFallback}
              />
              <video
                ref={refs[1]}
                {...framed(framing, `hero__video ${active === 1 ? "is-active" : ""}`)}
                playsInline
                muted
                autoPlay
                loop
                preload="none"
              />
            </>
          )}
          <div className="hero__overlay" />
        </div>
      )}

      {reduceMotion && posterFallback && (
        <picture className="hero__poster" aria-hidden={bgAlt ? undefined : "true"} {...ed.img("hero.media", { media: true })}>
          {!media && <source srcSet={posterSrc} type="image/webp" />}
          <img src={posterFallback} alt={bgAlt} {...framed(framing, "hero__poster-img-el")} fetchPriority="high" width={1280} height={720} />
        </picture>
      )}

      <div className="hero__container">
        <div
          ref={contentRef}
          className={`hero__content reveal reveal--up ${contentIn ? "in" : ""}`}
        >
          <E as="h1" className="heading reveal-item" style={{ transitionDelay: "120ms" }} path="hero.title" max={140}>
            {h.title}
          </E>
          <E as="p" className="description reveal-item" style={{ transitionDelay: "220ms" }} path="hero.subtitle" max={400}>
            {h.subtitle}
          </E>

          <div
            ref={ctaRef}
            className={`hero__actions reveal-item ${ctaIn ? "in" : ""}`}
            style={{ transitionDelay: "320ms" }}
          >
            <Button
              color="#0f1225"
              outline="#fff"
              className="hero__cta-btn"
              onClick={() => {
                // In the on-page editor a click edits the label instead of leaving the page
                if (!ed.editing) navigate(isFR ? "/fr/contact" : "/contact");
              }}
            >
              <Txt path="hero.ctaLabel" max={40}>{h.ctaLabel}</Txt>
            </Button>
            <a
              href={telHref(phone)}
              className="hero__call-btn"
              aria-label={`${h.callLabel}: ${phone}`}
            >
              {/* Phones: a short tap-to-call button. Desktop: the number itself, since clicking can't dial */}
              <E as="span" className="hero__call-short" path="hero.callLabel" max={40}>{h.callLabel}</E>
              <span className="hero__call-full">
                {/* Non-breaking hyphens keep the number on one line */}
                <Txt path="hero.callText" max={40}>{h.callText}</Txt> <E as="strong" path="hero.phone" shared max={20}>{ed.editing ? phone : phone.replace(/-/g, "\u2011")}</E>
              </span>
            </a>
          </div>

          <ul className="hero__trust reveal-item" style={{ transitionDelay: "420ms" }}>
            {(h.badges || []).map((b, i) => (
              <li key={i} {...ed.item("hero.badges", i, { name: "badge" })}>
                <Link to={isFR ? "/fr/conformite" : "/compliance"}><Txt path={["hero", "badges", i, "text"]} max={60}>{b.text}</Txt></Link>
              </li>
            ))}
            <E as="li" path="hero.lastBadge" max={60}>{h.lastBadge}</E>
          </ul>
        </div>
      </div>
    </section>
  );
};

export default Hero;
